import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import FormSection from './forms/FormSection';
import BusinessSection from './forms/BusinessSection';
import TeamSection from './forms/TeamSection';
import FundingSection from './forms/FundingSection';
import LegalSection from './forms/LegalSection';

class Phase3FormManager {
  constructor() {
    this.sections = new Map();
    this.flagDetector = new FlagDetector();
    this.autoPopulator = new AutoPopulator();
  }

  registerSection(name, section) {
    this.sections.set(name, section);
  }

  validateAll() {
    const errors = [];
    for (const [name, section] of this.sections) {
      const sectionErrors = section.validate();
      if (sectionErrors.length > 0) {
        errors.push({ section: name, errors: sectionErrors });
      }
    }
    return errors;
  }

  detectFlags(data) {
    return this.flagDetector.detectAll(data);
  }

  async populateFromPhase1(userId) {
    return await this.autoPopulator.populate(userId);
  }
}

class FlagDetector {
  detectAll(data) {
    const flags = {
      yellow: [],
      red: []
    };

    // Problem & Customer flags
    const problemFlags = this.detectProblemCustomerFlags(data);
    flags.yellow.push(...problemFlags.yellow);
    flags.red.push(...problemFlags.red);

    // Cap table flags
    const capTableFlags = this.detectCapTableFlags(data.capTableData, data.capTableTotals);
    flags.yellow.push(...capTableFlags.yellow);
    flags.red.push(...capTableFlags.red);

    // Team flags
    const teamFlags = this.detectTeamFlags(data);
    flags.yellow.push(...teamFlags.yellow);

    // Legal flags
    const legalFlags = this.detectLegalFlags(data);
    flags.yellow.push(...legalFlags.yellow);

    return flags;
  }

  detectProblemCustomerFlags(data) {
    const flags = { yellow: [], red: [] };
    
    if (!data.problemCustomer) return flags;
    
    const problemText = data.problemCustomer.toLowerCase();
    
    // Check for vague problem descriptions
    const vagueKeywords = [
      'small businesses',
      'everyone who',
      'people who want',
      'companies that',
      'businesses that need help',
      'anyone who',
      'users who like',
      'customers who use'
    ];
    
    const tooGeneralKeywords = [
      'more productive',
      'easier life',
      'better experience',
      'save time',
      'make money',
      'be successful',
      'grow their business'
    ];
    
    const lackingSpecificityKeywords = [
      'small businesses that need help with marketing',
      'companies that use excel too much',
      'gen z consumers who like technology',
      'people who want an easier life',
      'everyone who wants to be more productive'
    ];
    
    // Check for exact matches to known bad examples
    for (const badExample of lackingSpecificityKeywords) {
      if (problemText.includes(badExample)) {
        flags.yellow.push('Problem & Customer description is too vague → Needs specific role, company size, and pain point');
        return flags;
      }
    }
    
    // Check for vague language patterns
    let vagueCount = 0;
    for (const keyword of vagueKeywords) {
      if (problemText.includes(keyword)) {
        vagueCount++;
      }
    }
    
    for (const keyword of tooGeneralKeywords) {
      if (problemText.includes(keyword)) {
        vagueCount++;
      }
    }
    
    // Check for missing specificity indicators
    const hasSpecificRole = /\b(manager|owner|director|founder|ceo|cto|hr|operations|marketing|sales|designer|developer|freelance|consultant)\b/i.test(problemText);
    const hasCompanySize = /\b(\d+[-–]\d+|\d+\+|under \d+|small|medium|startup|enterprise)\b/i.test(problemText);
    const hasSpecificProblem = /\b(struggle|overwhelmed|hard to|difficult|pain|challenge|frustrated|time-consuming|manual|outdated|tracking|scheduling|managing|coordinating)\b/i.test(problemText);
    
    // Flag if too vague or missing key specificity
    if (vagueCount >= 2 || (!hasSpecificRole && !hasSpecificProblem)) {
      flags.yellow.push('Problem & Customer description is too vague → Needs specific role, company size, and pain point');
    } else if (problemText.length < 50) {
      flags.yellow.push('Problem & Customer description is too brief → Needs more detail about specific user and problem');
    }
    
    return flags;
  }

  detectCapTableFlags(capTableData, totals) {
    const flags = { yellow: [], red: [] };
    
    if (!capTableData || !totals || totals.grandTotal === 0) {
      return flags;
    }

    const { founders, employees, investors, grandTotal } = totals;
    
    // Calculate percentages
    const founderPercentage = (founders / grandTotal) * 100;

    // Flag: Founders collectively own <70%
    if (founderPercentage < 70) {
      flags.yellow.push('Founder/s collectively own <70% → Indicates too much early dilution or misaligned incentives');
    }

    // Flag: Single founder with no other team members and owns <80%
    if (capTableData.founders && capTableData.founders.length === 1 && founderPercentage < 80) {
      flags.yellow.push('Single founder with no other team members and owns <80% → Signals lack of team-building and outsized early dilution');
    }

    // Flag: Advisor(s) or consultants with >5% equity
    const advisorShares = capTableData.founders ? capTableData.founders.filter(f => 
      f.name && (f.name.toLowerCase().includes('advisor') || f.name.toLowerCase().includes('consultant'))
    ).reduce((sum, advisor) => sum + advisor.shares, 0) : 0;
    
    const advisorPercentage = (advisorShares / grandTotal) * 100;
    if (advisorPercentage > 5) {
      flags.yellow.push('Advisor(s) or consultants with >5% equity → Often suggests poor judgment or desperation in early deals');
    }

    // Flag: More than 5 investors already on CAP table
    const totalInvestors = capTableData.investors ? capTableData.investors.length : 0;
    if (totalInvestors > 5) {
      flags.yellow.push('More than 5 investors already on CAP table → Can signal poor fundraising hygiene and future cap table friction');
    }

    // Flag: Co-founders with drastically uneven ownership
    if (capTableData.founders && capTableData.founders.length >= 2) {
      const founderShares = capTableData.founders.map(f => f.shares);
      const founderPercentages = founderShares.map(shares => (shares / grandTotal) * 100);
      const maxDiff = Math.max(...founderPercentages) - Math.min(...founderPercentages);
      
      if (capTableData.founders.length === 2 && maxDiff > 20) {
        flags.yellow.push('Co-founders with drastically uneven ownership (>20% difference) → Indicates potential future disputes or lack of trust/collaboration');
      } else if (capTableData.founders.length >= 3 && maxDiff > 15) {
        flags.yellow.push('Co-founders with drastically uneven ownership (>15% difference) → Indicates potential future disputes or lack of trust/collaboration');
      }
    }

    // Flag: Inactive or departed co-founder still owns meaningful equity
    const inactiveFounders = capTableData.founders ? capTableData.founders.filter(f => 
      f.name && (
        f.name.toLowerCase().includes('(inactive)') ||
        f.name.toLowerCase().includes('(departed)') ||
        f.name.toLowerCase().includes('former') ||
        f.name.toLowerCase().includes('ex-') ||
        f.name.toLowerCase().includes('previous')
      )
    ) : [];
    
    for (const founder of inactiveFounders) {
      const founderPercentage = (founder.shares / grandTotal) * 100;
      if (founderPercentage > 10) {
        flags.yellow.push('Inactive or departed co-founder still owns meaningful equity (>10%) → Potential deadweight or cap table headache');
      }
    }

    return flags;
  }

  detectTeamFlags(data) {
    const flags = { yellow: [] };

    // Flag: Part-time capacity
    if (data.capacity === 'part-time') {
      flags.yellow.push('Part-time capacity → May indicate lack of commitment or competing priorities');
    }

    // Flag: Previous business partners
    if (data.previousPartners === 'yes') {
      flags.yellow.push('Previous business partners departed → Potential red flag for team dynamics');
    }

    return flags;
  }

  detectLegalFlags(data) {
    const flags = { yellow: [] };

    // Flag: Missing standard venture terms
    if (data.incorporated === 'yes') {
      if (!data.ipAssignment || !data.founderVesting || !data.founderBoardSeats) {
        flags.yellow.push('Missing standard venture terms → Corporate structure needs updating');
      }
      
      if (data.openToAmending === 'no') {
        flags.yellow.push('Not open to amending corporate documents → May block venture funding');
      }
    }

    // Flag: Not incorporated and wants to discuss
    if (data.incorporated === 'no' && data.incorporationAgreement === 'discuss') {
      flags.yellow.push('Would like to discuss incorporation → May indicate hesitation about standard terms');
    }

    return flags;
  }
}

class AutoPopulator {
  async populate(userId) {
    try {
      // Query the applications collection for this user's Phase 1 data
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', userId)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      if (!applicationsSnapshot.empty) {
        const userData = applicationsSnapshot.docs[0].data();
        return {
          companyName: userData.companyName || '',
          companyWebsite: userData.companyWebsite || '',
          // Add more fields as needed from Phase 1
        };
      }
    } catch (error) {
      console.error('Error populating from Phase 1:', error);
    }
    return {};
  }
}

const Phase3Form = () => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formManager] = useState(new Phase3FormManager());
  const [phase1Data, setPhase1Data] = useState({});
  const [hasExistingPhase3, setHasExistingPhase3] = useState(false);
  const [checkingPhase3, setCheckingPhase3] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [existingDraft, setExistingDraft] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    control
  } = useForm({
    defaultValues: {
      companyName: '',
      companyWebsite: '',
      incorporationLocation: '',
      productStage: '',
      tractionDetails: '',
      problemCustomer: '',
      videoPitchUrl: '',
      // Add more default values
    }
  });

  // Check if Phase 3 already submitted
  useEffect(() => {
    const checkExistingPhase3 = async () => {
      if (!user?.uid) {
        setCheckingPhase3(false);
        return;
      }

      try {
        const q = query(
          collection(db, 'applications'),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const application = querySnapshot.docs[0].data();
          if (application.phase3SubmittedAt) {
            setHasExistingPhase3(true);
          } else if (application.phase3InProgress) {
            // Load existing draft
            setIsDraft(true);
            setExistingDraft(application);
            // Auto-populate form with draft data
            Object.keys(application).forEach(key => {
              if (key.startsWith('company') || key.startsWith('product') || key.startsWith('problem') || 
                  key.startsWith('video') || key.startsWith('traction') || key.startsWith('capacity') || 
                  key.startsWith('previous') || key.startsWith('worked') || key.startsWith('equity') || 
                  key.startsWith('hasRaised') || key.startsWith('funding') || key.startsWith('incorporated') || 
                  key.startsWith('incorporation') || key.startsWith('ipAssignment') || key.startsWith('founder') || 
                  key.startsWith('openToAmending') || key.startsWith('capTable')) {
                setValue(key, application[key]);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error checking Phase 3 status:', error);
      } finally {
        setCheckingPhase3(false);
      }
    };

    if (user) {
      checkExistingPhase3();
      loadPhase1Data();
    }
  }, [user]);

  // Auto-save functionality
  useEffect(() => {
    if (!user?.uid || hasExistingPhase3) return;

    const autoSaveInterval = setInterval(async () => {
      const currentData = watch();
      
      // Check if there's any meaningful data to save
      const hasData = Object.values(currentData).some(value => 
        value && value !== '' && value !== null && value !== undefined
      );
      
      if (hasData) {
        setAutoSaving(true);
        try {
          await saveAsDraft(currentData, true); // true flag for auto-save
          setLastAutoSave(new Date());
        } catch (error) {
          console.error('Auto-save failed:', error);
        } finally {
          setAutoSaving(false);
        }
      }
    }, 60000); // Auto-save every minute

    return () => clearInterval(autoSaveInterval);
  }, [user, hasExistingPhase3, watch]);

  const loadPhase1Data = async () => {
    try {
      const data = await formManager.populateFromPhase1(user.uid);
      setPhase1Data(data);
      // Auto-populate form fields
      Object.keys(data).forEach(key => {
        setValue(key, data[key]);
      });
    } catch (error) {
      console.error('Error loading Phase 1 data:', error);
    }
  };

  const saveAsDraft = async (data, isAutoSave = false) => {
    if (!isAutoSave) {
      setSaving(true);
      setSubmitError('');
    }
    
    try {
      // Find the existing application record
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      if (applicationsSnapshot.empty) {
        if (!isAutoSave) {
          setSubmitError('No existing application found. Please complete Phase 1 first.');
          setSaving(false);
        }
        return;
      }

      const applicationDoc = applicationsSnapshot.docs[0];
      const applicationId = applicationDoc.id;

      // Clean data - remove undefined values
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== undefined && value !== null)
      );

      // Save as draft without validation
      const draftData = {
        ...cleanData,
        // Fix field name mapping for cap table only if they exist
        ...(data.capTableData && { capTable: data.capTableData }),
        ...(data.capTableTotals && { capTableTotals: data.capTableTotals }),
        phase3InProgress: true,
        phase3LastSaved: new Date(),
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'applications', applicationId), draftData);
      
      if (!isAutoSave) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      if (!isAutoSave) {
        setSubmitError('Failed to save draft. Please try again.');
      }
    } finally {
      if (!isAutoSave) {
        setSaving(false);
      }
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setSubmitError('');
    
    try {
      // Detect flags
      const flags = formManager.detectFlags(data);
      
      // Validate all sections
      const validationErrors = formManager.validateAll();
      if (validationErrors.length > 0) {
        setSubmitError('Please fix the errors in the form');
        setLoading(false);
        return;
      }

      // Find the existing application record
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid)
      );
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      if (applicationsSnapshot.empty) {
        setSubmitError('No existing application found. Please complete Phase 1 first.');
        setLoading(false);
        return;
      }

      const applicationDoc = applicationsSnapshot.docs[0];
      const applicationId = applicationDoc.id;

      // Clean data - remove undefined values
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== undefined && value !== null)
      );

      // Update the existing application with Phase 3 data
      const phase3UpdateData = {
        ...cleanData,
        // Fix field name mapping for cap table only if they exist
        ...(data.capTableData && { capTable: data.capTableData }),
        ...(data.capTableTotals && { capTableTotals: data.capTableTotals }),
        // Clear draft flags
        phase3InProgress: false,
        phase3LastSaved: null,
        // Set submission data
        phase3RedFlags: flags.red,
        phase3YellowFlags: flags.yellow,
        phase3SubmittedAt: new Date(),
        updatedAt: new Date(),
        currentPhase: 3,
        status: flags.red.length > 0 ? 'phase3_failed' : 
                flags.yellow.length > 0 ? 'phase3_pending' : 'phase3_approved'
      };

      await updateDoc(doc(db, 'applications', applicationId), phase3UpdateData);
      
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting Phase 3 application:', error);
      setSubmitError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingPhase3) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking Phase 3 status...</p>
        </div>
      </div>
    );
  }

  if (hasExistingPhase3) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Phase 3 Already Submitted</h2>
          <p className="text-gray-600 mb-6">
            You have already submitted your Phase 3 application. Check your dashboard for the current status.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Your Phase 3 application has been submitted successfully. You'll receive an email with next steps.
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Phase 3: In-Depth Application
              {isDraft && (
                <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  <i className="fas fa-edit mr-1"></i>
                  Draft Mode
                </span>
              )}
            </h1>
            <p className="text-gray-600">
              Please provide detailed information about your business, team, funding, and legal structure.
              {isDraft && (
                <span className="block mt-2 text-yellow-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  You're continuing from a saved draft. Your progress has been automatically restored.
                </span>
              )}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <BusinessSection 
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              phase1Data={phase1Data}
              formManager={formManager}
            />

            <TeamSection 
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              control={control}
              formManager={formManager}
            />

            <FundingSection 
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              control={control}
              formManager={formManager}
            />

            <LegalSection 
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              formManager={formManager}
            />

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{submitError}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={() => saveAsDraft(watch())}
                  disabled={saving}
                  className="px-6 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save as Draft'}
                </button>
                {(saveSuccess || autoSaving) && (
                  <div className="flex items-center text-green-600">
                    <i className={`fas ${autoSaving ? 'fa-spinner fa-spin' : 'fa-check-circle'} mr-2`}></i>
                    <span className="text-sm">
                      {autoSaving ? 'Auto-saving...' : 'Draft saved successfully!'}
                    </span>
                  </div>
                )}
                {lastAutoSave && !saveSuccess && !autoSaving && (
                  <div className="flex items-center text-gray-500">
                    <i className="fas fa-clock mr-2"></i>
                    <span className="text-xs">
                      Last saved: {lastAutoSave.toLocaleTimeString()}
                    </span>
                  </div>
                )}
                {isDraft && (
                  <div className="flex items-center text-yellow-600">
                    <i className="fas fa-info-circle mr-2"></i>
                    <span className="text-sm">Continuing from saved draft</span>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-blue-600 text-white text-lg font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Submitting...' : 'Submit Phase 3 Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Phase3Form;